import { useState, useEffect } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useTimer } from "@/context/TimerContext";
import { DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogClose } from "@/components/ui/dialog";
import { GuardedDialog, isDirty } from "@/components/support/GuardedDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { LOCATION_TYPES, BILLING_STATUSES } from "@/lib/supportApi";
import { toast } from "sonner";

// Shared dialog for starting a timer (mode="timer") or logging retrospective work (mode="manual").
export function TimeEntryDialog({ open, onOpenChange, mode = "manual", defaults = {}, onSaved }) {
  const { currentOrgId } = useAuth();
  const { start } = useTimer();
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({});
  const [init, setInit] = useState({});

  useEffect(() => {
    if (!open || !currentOrgId) return;
    const v = { customer_id: defaults.customer_id || "", ticket_id: defaults.ticket_id || "",
           machine_id: defaults.machine_id || "", category_key: "", location_type: "remote",
           description: "", minutes: "", billing_status: "unclassified" };
    setF(v); setInit(v);
    api.get(`/support/customers?org_id=${currentOrgId}&status=active`).then((r) => setCustomers(r.data)).catch(() => {});
    api.get(`/support/categories?org_id=${currentOrgId}`).then((r) => setCategories(r.data)).catch(() => {});
  }, [open, currentOrgId]); // eslint-disable-line

  useEffect(() => {
    if (f.customer_id && currentOrgId) {
      api.get(`/support/tickets?org_id=${currentOrgId}&customer_id=${f.customer_id}`).then((r) => setTickets(r.data)).catch(() => {});
    } else setTickets([]);
  }, [f.customer_id, currentOrgId]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.customer_id) { toast.error("Choose a customer"); return; }
    setSaving(true);
    try {
      if (mode === "timer") {
        await start({ customer_id: f.customer_id, ticket_id: f.ticket_id || null,
          machine_id: f.machine_id || null, category_key: f.category_key || null,
          location_type: f.location_type, description: f.description });
        toast.success("Timer started");
      } else {
        const actual_seconds = Math.round(parseFloat(f.minutes || "0") * 60);
        if (!actual_seconds) { toast.error("Enter minutes worked"); setSaving(false); return; }
        await api.post("/support/time-entries", { org_id: currentOrgId, customer_id: f.customer_id,
          ticket_id: f.ticket_id || null, machine_id: f.machine_id || null, category_key: f.category_key || null,
          location_type: f.location_type, description: f.description, actual_seconds,
          billing_status: f.billing_status });
        toast.success("Time entry saved");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <GuardedDialog open={open} onOpenChange={onOpenChange} dirty={isDirty(f, init)} className="bg-zinc-900 border-white/10 max-w-lg" testid="time-entry-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{mode === "timer" ? "Start timer" : "Add time entry"}</DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            {mode === "timer" ? "Track work as you do it." : "Record work you've already completed."} Time is never billed automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1 max-h-[65vh] overflow-y-auto">
          <div>
            <Label className="text-xs text-zinc-400">Customer *</Label>
            <Select value={f.customer_id} onValueChange={(v) => set("customer_id", v)}>
              <SelectTrigger data-testid="te-customer-select" className="mt-1 bg-black border-zinc-800"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {customers.map((c) => <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Ticket (optional)</Label>
              <Select value={f.ticket_id || "none"} onValueChange={(v) => set("ticket_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="te-ticket-select" className="mt-1 bg-black border-zinc-800"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  <SelectItem value="none">None</SelectItem>
                  {tickets.map((t) => <SelectItem key={t.ticket_id} value={t.ticket_id}>#{t.ticket_number} {t.subject}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Category</Label>
              <Select value={f.category_key} onValueChange={(v) => set("category_key", v)}>
                <SelectTrigger data-testid="te-category-select" className="mt-1 bg-black border-zinc-800"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {categories.map((c) => <SelectItem key={c.category_key} value={c.category_key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Location / type</Label>
              <Select value={f.location_type} onValueChange={(v) => set("location_type", v)}>
                <SelectTrigger data-testid="te-location-select" className="mt-1 bg-black border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {LOCATION_TYPES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {mode === "manual" && (
              <div>
                <Label className="text-xs text-zinc-400">Minutes worked *</Label>
                <Input data-testid="te-minutes-input" type="number" min="0" step="1" value={f.minutes}
                  onChange={(e) => set("minutes", e.target.value)} className="mt-1 bg-black border-zinc-800" placeholder="e.g. 30" />
              </div>
            )}
          </div>
          {mode === "manual" && (
            <div>
              <Label className="text-xs text-zinc-400">Billing classification</Label>
              <Select value={f.billing_status} onValueChange={(v) => set("billing_status", v)}>
                <SelectTrigger data-testid="te-billing-select" className="mt-1 bg-black border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {BILLING_STATUSES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs text-zinc-400">Work description</Label>
            <Textarea data-testid="te-description-input" value={f.description} onChange={(e) => set("description", e.target.value)}
              className="mt-1 bg-black border-zinc-800" rows={2} placeholder="What did you do?" />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="ghost" data-testid="te-cancel-button" className="text-zinc-400">Cancel</Button></DialogClose>
          <Button data-testid="te-submit-button" onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-500">
            {saving ? "Saving…" : (mode === "timer" ? "Start timer" : "Save entry")}
          </Button>
        </DialogFooter>
    </GuardedDialog>
  );
}
